import Papa from "papaparse";

/**
 * Field aliases for auto-mapping CSV columns to system fields
 * Keys are system field names, values are arrays of common variations (lowercase)
 */
export const FIELD_ALIASES = {
  email: [
    "email", "e-mail", "email_address", "emailaddress", "mail", 
    "email address", "correo", "correo electronico", "e_mail"
  ],
  first_name: [
    "first_name", "firstname", "first name", "fname", "given_name", 
    "givenname", "first", "nombre", "given name"
  ],
  last_name: [
    "last_name", "lastname", "last name", "lname", "surname", 
    "family_name", "familyname", "last", "apellido", "family name"
  ],
  phone: [
    "phone", "phone_number", "phonenumber", "telephone", "tel", 
    "mobile", "cell", "cellphone", "phone number", "telefono",
    "cell_phone", "mobile_phone", "work_phone", "home_phone"
  ],
  company_name: [
    "company", "company_name", "companyname", "organization", "org", 
    "business", "company name", "empresa", "organization_name", "employer"
  ],
  address: [
    "address", "street", "street_address", "address1", "address_line_1",
    "street address", "direccion", "address line 1", "mailing_address"
  ],
  city: [
    "city", "town", "municipality", "ciudad", "locality"
  ],
  state: [
    "state", "province", "region", "st", "estado", "state_province"
  ],
  postal_code: [
    "postal_code", "postalcode", "zip", "zipcode", "zip_code", 
    "postcode", "postal code", "zip code", "codigo_postal", "post_code"
  ],
};

/**
 * Number of rows to show in preview
 */
export const PREVIEW_ROW_COUNT = 5;

/**
 * Auto-map CSV headers to system fields based on common variations
 * @param {Array} headers - CSV headers [{name, value}, ...]
 * @param {Object} aliases - Field aliases mapping (defaults to FIELD_ALIASES)
 * @returns {Object} Auto-mapped field mappings {systemField: csvColumn, ...}
 */
export const autoMapFields = (headers, aliases = FIELD_ALIASES) => {
  const mappings = {};
  const usedHeaders = new Set();

  // For each system field, try to find a matching CSV header
  Object.entries(aliases).forEach(([systemField, variations]) => {
    for (const header of headers) {
      // Skip if this header is already mapped
      if (usedHeaders.has(header.value)) continue;

      const headerLower = header.value.toLowerCase().trim();
      
      // Check if header matches any variation
      if (variations.includes(headerLower)) {
        mappings[systemField] = header.value;
        usedHeaders.add(header.value);
        break;
      }
    }
  });

  return mappings;
};

/**
 * Get a sample value from the first row of contacts for a given column
 * @param {Array} contacts - Parsed CSV contacts
 * @param {string} columnName - Column name to get sample from
 * @param {number} maxLength - Maximum length before truncating (default 35)
 * @returns {string} Sample value or empty string
 */
export const getSampleValue = (contacts, columnName, maxLength = 35) => {
  if (!contacts?.length || !columnName) return "";
  
  // Try to find a non-empty value in the first few rows
  for (let i = 0; i < Math.min(contacts.length, 5); i++) {
    const value = contacts[i]?.[columnName];
    if (value && value.toString().trim()) {
      const strValue = value.toString().trim();
      return strValue.length > maxLength 
        ? strValue.slice(0, maxLength) + "..." 
        : strValue;
    }
  }
  
  return "";
};

/**
 * Get preview rows with mapped field names
 * @param {Array} contacts - Parsed CSV contacts
 * @param {Object} fieldMappings - Current field mappings {systemField: csvColumn}
 * @param {number} rowCount - Number of rows to preview (default PREVIEW_ROW_COUNT)
 * @returns {Array} Preview rows with system field names as keys
 */
export const getPreviewRows = (contacts, fieldMappings, rowCount = PREVIEW_ROW_COUNT) => {
  if (!contacts?.length) return [];
  
  const previewContacts = contacts.slice(0, rowCount);
  
  return previewContacts.map((contact, index) => {
    const previewRow = { _rowIndex: index + 1 };
    
    Object.entries(fieldMappings).forEach(([systemField, csvColumn]) => {
      if (csvColumn && contact[csvColumn] !== undefined) {
        previewRow[systemField] = contact[csvColumn];
      }
    });
    
    return previewRow;
  });
};

/**
 * Get mapped fields that have values (for preview table columns)
 * @param {Object} fieldMappings - Current field mappings
 * @returns {Array} Array of system field names that are mapped
 */
export const getMappedFields = (fieldMappings) => {
  return Object.entries(fieldMappings)
    .filter(([, csvColumn]) => csvColumn && csvColumn.trim())
    .map(([systemField]) => systemField);
};

/**
 * Parse a CSV file and return the parsed data
 * @param {File} file - The CSV file to parse
 * @returns {Promise<{data: Array, headers: Array, error: string|null}>}
 */
export const parseCSVFile = (file) => {
  return new Promise((resolve) => {
    if (!file) {
      resolve({ data: [], headers: [], error: "No file provided" });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (!result.data.length) {
          resolve({
            data: [],
            headers: [],
            error: "CSV file is empty or contains no valid records",
          });
          return;
        }

        // Filter out columns that don't have any data
        const columnsWithData = result.meta.fields.filter((field) => {
          return result.data.some((row) => {
            const value = row[field];
            return (
              value !== undefined &&
              value !== null &&
              value.toString().trim() !== ""
            );
          });
        });

        // Create headers array with name and value
        const headers = columnsWithData.map((field) => ({
          name: field,
          value: field,
        }));

        resolve({
          data: result.data,
          headers,
          error: null,
        });
      },
      error: (error) => {
        resolve({
          data: [],
          headers: [],
          error: error.message || "Failed to parse CSV file",
        });
      },
    });
  });
};

/**
 * Validate CSV file before parsing
 * @param {File} file - The file to validate
 * @param {number} maxSizeBytes - Maximum file size in bytes
 * @param {Array<string>} allowedTypes - Allowed MIME types
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validateCSVFile = (file, maxSizeBytes, allowedTypes) => {
  if (!file) {
    return { isValid: false, error: "No file selected" };
  }

  if (!allowedTypes.includes(file.type) && !file.name.endsWith(".csv")) {
    return { isValid: false, error: "Please upload a valid CSV file" };
  }

  if (file.size > maxSizeBytes) {
    const maxSizeMB = maxSizeBytes / (1024 * 1024);
    return {
      isValid: false,
      error: `File exceeds the ${maxSizeMB}MB file size limit`,
    };
  }

  return { isValid: true, error: null };
};

/**
 * Apply field mappings to parsed contacts
 * @param {Array} contacts - Raw parsed contacts from CSV
 * @param {Object} fieldMappings - Mapping of system fields to CSV columns
 * @returns {Array} Contacts with normalized field names
 */
export const applyFieldMappings = (contacts, fieldMappings) => {
  return contacts.map((contact) => {
    const mappedContact = {};

    Object.entries(fieldMappings).forEach(([systemField, csvColumn]) => {
      if (csvColumn && contact[csvColumn] !== undefined) {
        mappedContact[systemField] = contact[csvColumn];
      }
    });

    return mappedContact;
  });
};

/**
 * Get available CSV columns that haven't been mapped yet
 * @param {Array} headers - All CSV headers
 * @param {Object} fieldMappings - Current field mappings
 * @returns {Array} Available headers for mapping
 */
export const getAvailableColumns = (headers, fieldMappings) => {
  const mappedColumns = Object.values(fieldMappings).filter(Boolean);
  return headers.filter((header) => !mappedColumns.includes(header.value));
};

/**
 * Check if required fields are mapped
 * @param {Object} fieldMappings - Current field mappings
 * @param {Array} requiredFields - Array of required field names
 * @returns {{isValid: boolean, missingFields: Array}}
 */
export const validateRequiredMappings = (fieldMappings, requiredFields = ["email"]) => {
  const missingFields = requiredFields.filter(
    (field) => !fieldMappings[field] || fieldMappings[field].trim() === ""
  );

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};
